import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import { Checkbox, Chip } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import FormErrorHelperText from '../../../components/Form/FormErrorHelperText';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 14,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: '#1c2128',
    padding: 8,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'center',
      padding: 0,
      height: 44,
      borderRadius: 8,
      '& .tv-filled-icon': {
        marginRight: 4,
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'rgb(27 67 50 .15)',
      },
    },
    ...theme.applyStyles('light', {
      backgroundColor: '#fff',
    }),
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

const PopperComponent = props => {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
};

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({ theme }) => ({
  marginTop: 10,
  border: '1px solid #DCDCDE',
  borderRadius: 8,
  boxShadow: '0 10px 10px 0 rgb(16 10 85 / .1)',
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: '#c9d1d9',
  backgroundColor: '#1c2128',
  ...theme.applyStyles('light', {
    color: '#24292e',
    backgroundColor: '#fff',
  }),
}));

const StyledInput = styled(InputBase)({
  padding: 10,
  width: '100%',
  position: 'relative',
  '& svg': {
    left: '26px',
    position: 'absolute',
  },
  '& input': {
    borderRadius: 8,
    backgroundColor: '#F1F3F4',
    padding: '12px 16px 12px 48px',
    fontSize: 14,
  },
});

const Button = styled(ButtonBase)({
  fontSize: 16,
  width: '100%',
  textAlign: 'left',
  border: '1px solid #DEDEDE',
  color: '#757988',
  fontWeight: 500,
  justifyContent: 'space-between',
  padding: '12px 16px',
  height: 47,
  '& span': {
    width: '100%',
  },
  '& svg': {
    width: 16,
    height: 16,
  },
  ':focus-visible': {
    outline: '1px auto',
  },
});

const errorButtonStyles = {
  borderColor: '#d32f2f',
};

const MultiSelect = forwardRef(
  (
    {
      labelId,
      triggerId,
      options,
      renderInput,
      renderOption,
      renderChip,
      placeholder,
      onChange,
      isOptionEqualToValue,
      error,
      helperText,
      ...props
    },
    ref,
  ) => {
    const value = useMemo(() => {
      return props.value || [];
    }, [props.value]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingValue, setPendingValue] = useState(value);

    const handleChange = (event, newValue, reason) => {
      if (event.type === 'keydown' && event.key === 'Backspace' && reason === 'removeOption') {
        return;
      }
      setPendingValue(newValue);
    };

    const handleClick = event => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      onChange(pendingValue);
      if (anchorEl) {
        anchorEl.focus();
      }
      setAnchorEl(null);
    };

    const handleDelete = index => {
      const updatedValue = [...pendingValue];
      updatedValue.splice(index, 1);
      // setPendingValue(updatedValue);
      onChange(updatedValue);
    };

    useEffect(() => {
      setPendingValue(value);
    }, [value]);

    const open = Boolean(anchorEl);
    const id = open ? triggerId : undefined;

    return (
      <>
        <Box
          sx={{
            width: '100%',
            marginBottom: '8px',
          }}
        >
          <Button
            disableRipple
            aria-describedby={id}
            aria-labelledby={labelId}
            onClick={handleClick}
            sx={error && errorButtonStyles}
          >
            <span>{placeholder}</span>
            <svg viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M2.5 4.25L6 7.75L9.5 4.25'
                stroke='#9EA3AE'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Button>
          <FormErrorHelperText error={error}>{helperText}</FormErrorHelperText>
          <div className='flex gap-1 flex-wrap mt-[12px]'>
            {value?.map((chipValue, index) => renderChip(chipValue, index, handleDelete))}
          </div>
        </Box>
        <StyledPopper
          id={id}
          open={open}
          anchorEl={anchorEl}
          sx={{
            width: anchorEl?.clientWidth,
          }}
          placement='bottom-start'
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <div>
              <Autocomplete
                {...props}
                ref={ref}
                open
                multiple
                onClose={(_event, reason) => {
                  if (reason === 'escape') {
                    handleClose();
                  }
                }}
                value={pendingValue}
                onChange={handleChange}
                disableCloseOnSelect
                renderTags={() => null}
                noOptionsText='No options'
                renderOption={renderOption}
                isOptionEqualToValue={isOptionEqualToValue}
                options={[...options].sort((a, b) => {
                  // Display the selected labels first.
                  let ai = value.find(val => val.value === a.value);
                  ai = ai === -1 ? value.length + ai : ai;
                  let bi = value.find(val => val.value === b.value);
                  bi = bi === -1 ? value.length + bi : bi;
                  return ai - bi;
                })}
                getOptionLabel={option => option.label || option.value}
                renderInput={params => renderInput({ ...params })}
                slots={{
                  popper: PopperComponent,
                }}
              />
            </div>
          </ClickAwayListener>
        </StyledPopper>
      </>
    );
  },
);

MultiSelect.propTypes = {
  labelId: PropTypes.string,
  value: PropTypes.array,
  onChange: PropTypes.func,
  triggerId: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  renderInput: PropTypes.func,
  renderOption: PropTypes.func,
  renderChip: PropTypes.func,
  watch: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

MultiSelect.defaultProps = {
  placeholder: 'Select',
  options: [],
  renderInput: params => (
    <StyledInput
      ref={params.InputProps.ref}
      inputProps={params.inputProps}
      name={params.name}
      startAdornment={
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M7.66671 13.9999C11.1645 13.9999 14 11.1644 14 7.66659C14 4.16878 11.1645 1.33325 7.66671 1.33325C4.1689 1.33325 1.33337 4.16878 1.33337 7.66659C1.33337 11.1644 4.1689 13.9999 7.66671 13.9999Z'
            stroke='#9EA3AE'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M14.6667 14.6666L13.3334 13.3333'
            stroke='#9EA3AE'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      }
      autoFocus
      placeholder='Search..'
    />
  ),
  renderOption: (props, option, { selected }) => (
    <li {...props}>
      <Checkbox checked={selected} />
      <span>{option.label}</span>
    </li>
  ),
  renderChip: (option, i, handleDelete) => (
    <Chip
      key={i}
      label={option.label}
      onDelete={() => handleDelete(i)}
      sx={{
        backgroundColor: 'transparent',
        border: '1px solid #DADADA',
        borderRadius: 32,
        height: 36,
        fontSize: 14,
      }}
    />
  ),
  isOptionEqualToValue: (option, value) => option.value === value.value,
};

export default MultiSelect;
